import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import monetaryAccountService from '../services/monetaryAccountService';
import { useNotification } from '../context/NotificationContext';
import '../styles/general.css';

const EMPTY_FORM = {
  name: '',
  account_type: 'bank',
  location: '',
  account_number: '',
  clabe: ''
};

/**
 * Create and edit a monetary account.
 *
 * The type select governs the form: a cash account has no account number and
 * no CLABE, so those inputs disappear AND their values are cleared. Clearing
 * matters as much as hiding — a hidden value that rides along in the payload
 * is nulled server-side, and the user would never learn what was saved.
 *
 * The validation here duplicates the serializer's on purpose: it exists to
 * give a fast answer, not to be the guarantee.
 */
const MonetaryAccountFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { notify } = useNotification();

  const isEditMode = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  const loadAccount = useCallback(async () => {
    try {
      const response = await monetaryAccountService.getAccountById(id);
      const data = response.data;
      setForm({
        name: data.name || '',
        account_type: data.account_type || 'bank',
        location: data.location || '',
        account_number: data.account_number || '',
        clabe: data.clabe || ''
      });
    } catch (err) {
      console.error('Error loading monetary account:', err);
      notify.error(t('accounting.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    if (isEditMode) loadAccount();
  }, [isEditMode, loadAccount]);

  const isCash = form.account_type === 'cash';

  const handleChange = (field) => (event) => {
    const { value } = event.target;

    setForm((current) => {
      if (field !== 'account_type') {
        return { ...current, [field]: value };
      }

      // Switching to cash empties the banking fields rather than merely
      // hiding them, so what the user sees is what gets sent.
      if (value === 'cash') {
        return { ...current, account_type: value, account_number: '', clabe: '' };
      }

      return { ...current, account_type: value };
    });

    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    const found = {};

    if (!form.name.trim()) found.name = t('accounting.nameRequired');
    if (!form.location.trim()) found.location = t('accounting.locationRequired');

    if (!isCash && !form.account_number.trim()) {
      found.account_number = t('accounting.accountNumberRequired');
    }

    if (!isCash && form.clabe.trim() && !/^\d{18}$/.test(form.clabe.trim())) {
      found.clabe = t('accounting.clabeInvalid');
    }

    return found;
  };

  const applyServerErrors = (err) => {
    const data = err?.response?.data;

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      // Field errors land under their field. The name clash with a
      // deactivated account is the message that most needs to arrive
      // verbatim, because it tells the user to reactivate rather than
      // rename.
      const mapped = {};
      Object.entries(data).forEach(([field, messages]) => {
        mapped[field] = Array.isArray(messages) ? messages.join(' ') : String(messages);
      });
      setErrors(mapped);
      return;
    }

    notify.error(t('accounting.failedToSave'));
  };

  const handleSave = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      if (isEditMode) {
        await monetaryAccountService.updateAccount(id, form);
      } else {
        await monetaryAccountService.createAccount(form);
      }
      notify.success(t('accounting.saved'));
      navigate('/monetary-accounts');
    } catch (err) {
      console.error('Error saving monetary account:', err);
      applyServerErrors(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{isEditMode ? t('accounting.editTitle') : t('accounting.createTitle')}</h1>
        {isEditMode && form.name && (
          <div className="order-number-badge">
            <span className="order-number-label">{t('accounting.accountName')}</span>
            <span className="order-number-value">{form.name}</span>
          </div>
        )}
      </div>

      <fieldset className="form-fieldset">
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label" htmlFor="accountName">
              {t('accounting.accountName')}
            </label>
            <input
              id="accountName"
              type="text"
              value={form.name}
              onChange={handleChange('name')}
              className="form-input"
            />
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="accountType">
              {t('accounting.accountType')}
            </label>
            <select
              id="accountType"
              value={form.account_type}
              onChange={handleChange('account_type')}
              className="form-select"
            >
              <option value="bank">{t('accounting.types.bank')}</option>
              <option value="cash">{t('accounting.types.cash')}</option>
            </select>
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label" htmlFor="accountLocation">
              {t('accounting.location')}
            </label>
            <input
              id="accountLocation"
              type="text"
              value={form.location}
              onChange={handleChange('location')}
              className="form-input"
            />
            {errors.location && <div className="form-error">{errors.location}</div>}
          </div>
        </div>

        {!isCash && (
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="accountNumber">
                {t('accounting.accountNumber')}
              </label>
              <input
                id="accountNumber"
                type="text"
                value={form.account_number}
                onChange={handleChange('account_number')}
                className="form-input"
              />
              {errors.account_number && (
                <div className="form-error">{errors.account_number}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="accountClabe">
                {t('accounting.clabe')}
              </label>
              <input
                id="accountClabe"
                type="text"
                inputMode="numeric"
                value={form.clabe}
                onChange={handleChange('clabe')}
                className="form-input"
              />
              {errors.clabe && <div className="form-error">{errors.clabe}</div>}
            </div>
          </div>
        )}
      </fieldset>

      <div className="form-actions">
        <button
          type="button"
          onClick={() => navigate('/monetary-accounts')}
          className="btn btn-secondary"
        >
          {t('accounting.backToList')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
};

export default MonetaryAccountFormPage;
